import MonitorDiffText from './_monitors_diff_text';
import Icon from './_icon';
import Dropdown from './_dropdown';
import DropdownMenu from './_dropdown_menu';
import PropTypes from 'prop-types';
import classNames from 'classnames';

class Monitor extends React.Component {

  static contextTypes = {
    api: PropTypes.object.isRequired
  };

  static propTypes = {
    id: PropTypes.number.isRequired,
    highlightId: PropTypes.number.isRequired,
    lastUpdate: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    handleExpand: PropTypes.func.isRequired,
    handleDelete: PropTypes.func.isRequired,
    isExpanded: PropTypes.bool,
    preExpanded: PropTypes.bool
  };

  static defaultProps = {
    isExpanded: false,
    preExpanded: false
  };

  state = {
    currentCompare: {},
    data: null,
    expandedMonitorView: {
      IMAGE: 1,
      TEXT: 0
    },
    isExpanded: this.props.isExpanded
  };

  componentDidMount() {
    if(this.props.preExpanded) {
      this.handleExpand();
    }
  }

  loadText = ({highlight, onSuccess}) => {
    this.context.api.monitorContentsGet(highlight.id, highlightContents => {
      highlight.bodyText = highlightContents.bodyText || '';
      this.setState(prevState => {
        const highlightIdx = prevState.data.findIndex(hl => {
          return hl.id === highlight.id;
        });

        if(highlight.bodyText === '' || highlight.bodyText === 'undefined') {
          prevState.data[highlightIdx].bodyText = 'An error occurred.';
        }
        else {
          prevState.data[highlightIdx].bodyText = highlight.bodyText;
        }

        return {data: prevState.data};
      });

      if(typeof onSuccess === 'function') {
        onSuccess.call(this, highlightContents);
      }
    });
  };

  loadMonitorDetailsContent = (callback = () => {}) => {
    const data = this.state.data ? this.state.data.slice() : [];

    if(data.length === 0) {
      this.loadMonitorDetails();

      return;
    }
    else if(data.length < 2) {
      return;
    }

    const {highlightId} = data[0];
    const {idx} = this.state.currentCompare[highlightId] || {idx: 1};

    if(!data[0].bodyText) {
      // load newest text
      this.loadText({highlight: data[0], onSuccess: monitorDetails => {
        this.setState(prevState => {
          const curr = prevState.currentCompare[highlightId] ? prevState.currentCompare[highlightId] : {idx: 1, showOnlyChanges: true};

          return {
            currentCompare: {
              [highlightId]: {
                idx: curr.idx,
                showOnlyChanges: curr.showOnlyChanges,
                newItem: monitorDetails,
                oldItem: curr.oldItem || null
              }
            }
          };
        });
      }});
    }

    if(!data[idx].bodyText) {
      // load compare text
      this.loadText({highlight: data[idx], onSuccess: monitorDetails => {
        this.setState(prevState => {
          const curr = prevState.currentCompare[highlightId] ? prevState.currentCompare[highlightId] : {idx: 1, showOnlyChanges: true};

          return {
            currentCompare: {
              [highlightId]: {
                idx: curr.idx,
                newItem: curr.newItem || null,
                oldItem: monitorDetails,
                showOnlyChanges: curr.showOnlyChanges
              }
            }
          };
        }, callback);
      }});
    }
  };

  loadMonitorDetails = () => {
    const {id: monitorId = 0, highlightId = 0} = this.props;

    // check cache first
    if(!this.state.data) {
      this.context.api.monitorGet(monitorId, {
        sort: 'created_at',
        sortDir: 'desc',
        limit: 10,
        startDate: '2000-01-01'
      }, monitorData => {
        let data = monitorData ? monitorData.slice() : [];

        if(highlightId) {
          data = data.filter(highlight => highlight.highlightId === highlightId);
        }

        // ensure reverse chronological order
        data.sort((a, b) => {
          const createdA = new Date(a.createdAt);
          const createdB = new Date(b.createdAt);

          if(createdA < createdB) {
            return -1;
          }

          if(createdA > createdB) {
            return 1;
          }

          return 0;
        }).reverse();

        this.setState({
          data
        }, () => {
          this.loadMonitorDetailsContent();
        });
      });
    }
  };

  deleteMonitor = () => {
    const {id: monitorId = 0} = this.props;

    this.context.api.monitorDelete(monitorId, status => {
      this.props.handleDelete(monitorId);
      console.log('Monitor.deleteMonitor: deleted monitorId #%o: %o', monitorId, status);
    });
  };

  handleExpand = () => {
    const {highlightId = 0, preExpanded} = this.props;
    const {isExpanded, expandedMonitorView} = this.state;

    if(!isExpanded || preExpanded) {
      // load data
      this.loadMonitorDetails();
    }

    this.setState({
      isExpanded: !isExpanded,
      expandedMonitorView
    }, () => {
      this.props.handleExpand(highlightId);
    });
  };

  scrollToFirstTextChange = () => {
    if(this.state.expandedMonitorView.TEXT) {
      // requires setTimeout 0 to allow el.querySelector to reliably work
      // on the <span> that contains the page src
      setTimeout(() => {
        const el = this.refs.textDiff;

        if(el) {
          const firstEdit = el.querySelector('del, ins');
          const scroll = firstEdit ? firstEdit.offsetTop - el.offsetTop : 0;

          el.scrollTop = scroll >= 0 ? scroll : 0;
        }
      }, 0);
    }
  };

  handleMonitorModeClick = view => {
    this.setState({
      expandedMonitorView: {[view]: 1}
    }, () => {
      if(this.state.expandedMonitorView.TEXT) {
        this.scrollToFirstTextChange();
      }
    });
  };

  renderImageDiff = (highlightId, nav) => {
    const data = this.state.data ? this.state.data.slice() : [];

    if(data.length < 2) {
      return;
    }

    const newItem = data[0];
    const current = this.state.currentCompare[highlightId] ? this.state.currentCompare[highlightId] : {idx: 1, showOnlyChanges: true};
    const oldItem = data[current.idx];
    const snapshots = [];
    const screenShotUrlNew = newItem.screenShotUrl;
    const screenShotUrlOld = oldItem.screenShotUrl;
    const dateA = moment(oldItem.createdAt).format('MMM D, YYYY');
    const dateB = moment(newItem.createdAt).fromNow();

    snapshots.push(
      <div className="monitor-image monitor-image--new" key={`image${newItem.id}`} ref={`monitor${highlightId}-snapshot${newItem.id}`}>
        <div className="monitor-image_item">
          <a target="_blank" href={screenShotUrlNew}>
            <img
              className="monitor-image-image"
              src={screenShotUrlNew}
              onError={event => {
                event.target.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
                event.target.classList.add('monitor-image-image--error');
              }}
              loading="lazy" />
          </a>
        </div>
      </div>
    );

    snapshots.push(
      <div className="monitor-image monitor-image--old" key={`image${oldItem.id}`} ref={`monitor${highlightId}-snapshot${oldItem.id}`}>
        <div className="monitor-image_item">
          <a target="_blank" href={screenShotUrlOld}>
            <img
              className="monitor-image-image"
              onError={event => {
                event.target.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
                event.target.classList.add('monitor-image-image--error');
              }}
              onLoad={() => {
                this.setState(prevState => {
                  const highlightIdx = prevState.data.findIndex(hl => {
                    return hl.id === oldItem.id;
                  });

                  prevState.data[highlightIdx].loaded = true;
                  oldItem.loaded = true;

                  return {data: prevState.data};
                });
              }}
              src={screenShotUrlOld}
              loading="lazy" />
          </a>
        </div>
      </div>
    );

    const uiColumnB = snapshots.shift();
    const uiColumnA = snapshots;
    const loading = (oldItem.loaded) ? null : (<div className="monitors-loading-spinner" key="diffLoader" />);

    const monitorClasses = classNames('monitor-image-diff', {
      'monitor-image-diff--loading': !oldItem.loaded
    });

    return (
      <div key={highlightId}>
        <div className="monitor-labels">
          <div className="monitor-columnA">
            {nav}
            <div className="monitor-labels-date xtra">{dateA}</div>
          </div>
          <div className="monitor-columnB">
            <div className="monitor-labels-date xtra">{dateB}</div>
          </div>
        </div>
        <div ref={`monitor-image-diff${highlightId}`} className={monitorClasses}>
          <div className="monitor-columnA">{loading}{uiColumnA}</div>
          <div className="monitor-columnB">{uiColumnB}</div>
        </div>
      </div>
    );
  };

  toggleTextChanges = highlightId => {
    this.setState(prevState => {
      const curr = prevState.currentCompare[highlightId] ? prevState.currentCompare[highlightId] : {idx: 1, showOnlyChanges: true};

      return {
        currentCompare: {
          [highlightId]: {
            idx: curr.idx,
            showOnlyChanges: !curr.showOnlyChanges
          }
        }
      };
    }, this.scrollToFirstTextChange);
  };

  renderTextDiff = (highlightId, nav) => {
    const data = this.state.data ? this.state.data.slice() : [];

    if(data.length < 2) {
      return;
    }

    const newItem = data[0];
    const current = this.state.currentCompare[highlightId] ? this.state.currentCompare[highlightId] : {idx: 1, showOnlyChanges: true};
    const oldItem = data[current.idx];
    const {showOnlyChanges} = current;
    let diff = null;
    const newText = newItem.bodyText;
    const oldText = oldItem.bodyText;

    if(!newText || !oldText) {
      this.loadMonitorDetailsContent(() => {
        diff = <MonitorDiffText textA={oldText} textB={newText} ref="monitorDiffText" />;
      });
    }
    else {
      diff = <MonitorDiffText textA={oldText} textB={newText} ref="monitorDiffText" />;
    }

    const toggleTextChangesClass = classNames('toggle-icon fa', {
      'fa-toggle-on': showOnlyChanges,
      'fa-toggle-off': !showOnlyChanges
    });
    const tooltipToggleTextChanges = showOnlyChanges ? 'Show all' : 'Show only changes';
    const toggleTextChanges = (
      <div className="toggle-changes" key={`toggle-changes${highlightId}`}>
        <span onClick={() => this.toggleTextChanges(highlightId)}>
          <i className={toggleTextChangesClass} data-class="tooltip" data-tip={tooltipToggleTextChanges} />
          <label className="toggle-changes-label xtra" data-class="tooltip" data-tip={tooltipToggleTextChanges}>
            Changes only
          </label>
        </span>
      </div>
    );

    return (
      <div key={`textDiff${highlightId}`}>
        <div className="monitor-labels">
          <div className="monitor-columnA">{nav}</div>
          <div className="monitor-columnB">{toggleTextChanges}</div>
        </div>
        <div className={`monitor-text-diff ${showOnlyChanges ? 'edits-only' : ''}`} ref="textDiff">
          <div className="monitor-text-diff_inner">{diff}</div>
        </div>
      </div>
    );
  };

  renderMonitorNav = () => {
    const data = this.state.data ? this.state.data.slice() : false;
    const dropDownValues = [];
    const {highlightId} = data[0];
    const curr = this.state.currentCompare[highlightId] ? this.state.currentCompare[highlightId] : {idx: 0, showOnlyChanges: true};
    const {idx, showOnlyChanges} = curr;
    const newItem = data[0];
    const oldItem = data[idx];

    if(!data) {
      return;
    }

    data.forEach((snapshot, i) => {
      if(i > 0) {
        dropDownValues.push([i, `${moment(snapshot.createdAt).format('MMM D, YYYY')}`]);
      }
    });

    const nav =
      data.length > 2 ? (
        <DropdownMenu
          id="diffDropdown"
          label={`${moment(newItem.createdAt).fromNow()}`}
          title="Previous snapshots"
          values={dropDownValues}
          selectedValue={idx}
          selectCallback={value => {
            this.setState({
              currentCompare: {
                [highlightId]: {
                  idx: value,
                  showOnlyChanges,
                  newItem,
                  oldItem: data[value]
                }
              }
            }, () => {
              this.loadMonitorDetailsContent(this.scrollToFirstTextChange);
            });
          }} />
      ) : (
        <div className="monitor-labels-label">
          <span className="xtra">{moment(newItem.createdAt).fromNow()}</span>
          {moment(oldItem.createdAt).format('MMM D, YYYY')}
        </div>
      );

    return nav;
  };

  renderMonitorExpanded = () => {
    const data = this.state.data ? this.state.data.slice() : false;
    let uiDiffBody;

    if(data === false) {
      // loading
      uiDiffBody = [
        <div className="monitors-loading-spinner" key="diffLoader" />
      ];
    }
    else if(Array.isArray(data) && data.length) {
      const {highlightId} = data[0];

      uiDiffBody = [];

      if(this.state.expandedMonitorView.IMAGE) {
        uiDiffBody.push(this.renderImageDiff(highlightId, this.renderMonitorNav()));
      }
      else {
        uiDiffBody.push(this.renderTextDiff(highlightId, this.renderMonitorNav()));
      }
    }

    return (
      <div className="monitor-item_expanded">
        <div className="tabs">
          <div
            onClick={() => {
              this.handleMonitorModeClick('IMAGE');
            }}
            className={'tabs_tab ' + (this.state.expandedMonitorView.IMAGE ? 'tabs_tab--active' : '')}>
            <span className="tabs_tab_title">
              Image <span className="xtra">compare</span>
            </span>
          </div>
          <div
            onClick={() => {
              this.handleMonitorModeClick('TEXT');
            }}
            className={'tabs_tab ' + (this.state.expandedMonitorView.TEXT ? 'tabs_tab--active' : '')}>
            <span className="tabs_tab_title">
              Text <span className="xtra">compare</span>
            </span>
          </div>
        </div>
        {uiDiffBody}
      </div>
    );
  };

  render() {
    const uiDropdownOptions = [{value: 0, label: 'Delete Monitor & History', icon: 'fa-trash', onOptionClick: this.deleteMonitor, active: false}];
    const {isExpanded, id, highlightId, lastUpdate, title, type, url} = this.props;
    const urlDisplay = (url || '')
      .split('#')[0]
      .split('?')[0]
      .replace(/https?:\/+(w+\.)?/, '')
      .replace(/\/$/, '')
      .replace(/\/(.*)\//, '/…/');
    const monitorClasses = classNames('monitor-item', {
      'monitor-item--expanded': isExpanded
    });
    let uiMonitorExpanded = null;

    if(isExpanded) {
      uiMonitorExpanded = this.renderMonitorExpanded();
    }

    return (
      <div className={monitorClasses} key={`monitor${id}`} ref={`resultRow-${highlightId}`}>
        <div className="monitor-item_details" onClick={this.handleExpand}>
          <div className="monitor-meta">
            <div className="monitor-meta_col-date">
              <div className="monitor-date" data-class="tooltip" data-tip={`Last change: ${lastUpdate}`}>
                {lastUpdate}
              </div>
            </div>
            <div className="monitor-meta_col-type">
              <Icon
                icon={type === 'highlights' ? 'detail' : 'fullpage'}
                width="18"
                height="18"
                data-class="tooltip"
                data-tip={type === 'highlights' ? 'Type: Highlight' : 'Type: Full page'} />
            </div>
            <div className="monitor-meta_col-details">
              <div className="monitor-title">{title}</div>
              <div className="monitor-source">
                <a href={url} target="_blank">
                  {urlDisplay}
                </a>
              </div>
            </div>
          </div>
          <div className="monitor-item_details_menu">
            <Dropdown
              options={uiDropdownOptions}
              condensed={true}
              containerClass="ui-dropdown-flush ui-dropdown-no-border ui-dropdown-card-style"
              className="btn btn-dropdown-card btn-dropdown-size-38" />
          </div>
        </div>
        {uiMonitorExpanded}
      </div>
    );
  }

}

export default Monitor;
